import { inject, Injector } from '@angular/core';
import { HttpInterceptorFn, HttpRequest, HttpHandlerFn, HttpEvent } from '@angular/common/http';
import { AuthStore } from '../stores/auth-store';
import { Observable, from } from 'rxjs';
import { switchMap, catchError } from 'rxjs/operators';
import { devLog, devWarn } from '@app/core/utils/dev-logger';

/**
 * JWT Interceptor
 *
 * Automatically adds JWT token to outgoing requests.
 * If token will expire soon (< 5 minutes), triggers proactive refresh.
 *
 * Flow:
 * 1. Check if user has a token
 * 2. If token expires soon, refresh it first
 * 3. Add Authorization header with (potentially refreshed) token
 * 4. If refresh fails, let request proceed (will trigger 401 → http-error interceptor)
 *
 * Note: Uses Injector to safely get AuthStore to avoid circular dependency during APP_INITIALIZER
 * (translation loading uses HTTP, which triggers interceptors)
 */

export const jwtInterceptor: HttpInterceptorFn = (
  req: HttpRequest<any>,
  next: HttpHandlerFn
): Observable<HttpEvent<any>> => {
  // Inject Injector at interceptor level (valid injection context)
  const injector = inject(Injector);

  // Try to get AuthStore using Injector
  let authStore: AuthStore;
  try {
    authStore = injector.get(AuthStore);
  } catch (error) {
    // AuthStore not available yet (during initialization)
    // Let request proceed without auth
    return next(req);
  }

  const token = authStore.token();

  // No token - let request proceed without auth
  if (!token) {
    return next(req);
  }

  // Don't refresh for the refresh endpoint itself (prevents infinite loop)
  const isRefreshEndpoint = req.url.includes('/auth/refresh');

  // Check if token will expire soon (within 5 minutes = 300 seconds)
  // This gives us plenty of time to refresh before actual expiration
  const willExpireSoon = !isRefreshEndpoint && authStore.willExpireSoon(token, 300);

  if (willExpireSoon) {
    devLog('🔄 JWT Interceptor: Token expiring soon (< 5 min), refreshing proactively...');

    return from(authStore.refreshIfExpiringSoon()).pipe(
      switchMap(() => {
        const newToken = authStore.token();

        if (!newToken) {
          devWarn('⚠️ JWT Interceptor: Refresh failed, no token available');
          // Let request proceed without auth - will trigger 401 → http-error interceptor
          return next(req);
        }

        devLog('✅ JWT Interceptor: Token refreshed, adding to request');
        const cloned = req.clone({
          setHeaders: { Authorization: `JWT ${newToken}` }
        });
        return next(cloned);
      }),
      catchError(error => {
        devWarn('⚠️ JWT Interceptor: Token refresh failed:', error);
        // Let request proceed without auth - will trigger 401 → http-error interceptor
        return next(req);
      })
    );
  }

  // Token is valid - add to request
  const cloned = req.clone({
    setHeaders: { Authorization: `JWT ${token}` }
  });
  return next(cloned);
};
